(() => {
  const config = JSON.parse(document.getElementById('calendar-presale-config').textContent);
  const variantIds = new Set();
  const products = new Map();
  function registerProduct(product) {
    if (!product.handle) return;
    products.set(product.handle, product.earlyBird);
    if (product.earlyBird) (product.variantIds || []).forEach(id => variantIds.add(String(id)));
  }
  (config.products || []).forEach(registerProduct);

  // Cart JSON has no tags. Resolve unknown products from Shopify, never from line properties.
  async function classifyCart(cart) {
    if (!active()) return cart;
    await Promise.all([...new Set(cart.items.map(item => item.handle))].map(async handle => {
      if (products.has(handle)) return;
      if (!handle) throw new Error(config.errorMessage);
      const response = await fetch(`${config.productsRoot}${encodeURIComponent(handle)}.js`);
      if (!response.ok) throw new Error(config.errorMessage);
      const product = await response.json();
      if (!Array.isArray(product.tags) || !Array.isArray(product.variants)) throw new Error(config.errorMessage);
      registerProduct({ handle, earlyBird: product.tags.includes('early-bird'), variantIds: product.variants.map(variant => variant.id) });
    }));
    return cart;
  }
  let pending = Promise.resolve();
  let checkoutBlocked = false;

  // Membership is purely tag-based; the switch is only an emergency stop.
  function active() {
    return !!config.enabled;
  }

  function isCalendar(id) {
    if (variantIds.has(String(id))) return true;
    // Includes cards and quick views injected after the initial page render.
    return [...document.querySelectorAll('[data-early-bird-variants]')].some(element =>
      JSON.parse(element.dataset.earlyBirdVariants).map(String).includes(String(id)));
  }

  function isCalendarItem(item) {
    return products.get(item.handle) === true || isCalendar(item.variant_id);
  }

  function hasCalendar(cart) {
    return active() && cart.items.some(isCalendarItem);
  }

  function hasConflict(cart) {
    return hasCalendar(cart) && cart.items.some(item => !isCalendarItem(item));
  }

  async function readCart() {
    const response = await fetch(`${config.cartUrl}.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error(config.errorMessage);
    return classifyCart(await response.json());
  }

  // Serialize writes and the explicit "keep" choice, including across tabs where supported.
  function enqueue(task) {
    const run = () => navigator.locks ? navigator.locks.request('heimat-calendar-cart', task) : task();
    const result = pending.then(run);
    pending = result.catch(() => {});
    return result;
  }

  function syncCheckout(cart) {
    checkoutBlocked = active() && (!cart || hasConflict(cart));
    document.querySelectorAll('[name="checkout"]').forEach(button => {
      if (checkoutBlocked) {
        if (!button.disabled) button.setAttribute('data-calendar-blocked', '');
        button.disabled = true;
      } else if (button.hasAttribute('data-calendar-blocked')) {
        button.disabled = !!cart && cart.items.length === 0;
        button.removeAttribute('data-calendar-blocked');
      }
    });
    document.querySelectorAll('.shopify-payment-button, .additional-checkout-buttons').forEach(element => {
      if (checkoutBlocked && !element.hidden) {
        element.hidden = true;
        element.setAttribute('data-calendar-express-hidden', '');
      } else if (!checkoutBlocked && element.hasAttribute('data-calendar-express-hidden')) {
        element.hidden = false;
        element.removeAttribute('data-calendar-express-hidden');
      }
    });
  }

  function showCartError() {
    // Errors stay in the cart, never in a toast or a second PDP message.
    const container = document.querySelector('cart-drawer.active .cart__ctas, #main-cart-footer .cart__ctas, cart-drawer .cart__ctas');
    if (!container || container.parentElement.querySelector('[data-calendar-error]')) return;
    const error = document.createElement('p');
    error.className = 'calendar-cart-error';
    error.setAttribute('data-calendar-error', '');
    error.setAttribute('role', 'alert');
    error.textContent = config.errorMessage;
    container.before(error);
  }

  async function refreshCart(cart, openDrawer = false) {
    const drawer = document.querySelector('cart-drawer');
    const ids = new Set(drawer ? drawer.getSectionsToRender().map(section => section.id) : ['cart-icon-bubble']);
    ['main-cart-items', 'main-cart-footer'].forEach(id => {
      if (document.getElementById(id)) ids.add(id);
    });
    const response = await fetch(`${config.cartUrl}?sections=${[...ids].join(',')}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(config.errorMessage);
    const sections = await response.json();
    if (drawer) {
      if (!sections['cart-drawer']) throw new Error(config.errorMessage);
      drawer.renderContents({ sections });
    }
    for (const id of ['main-cart-items', 'main-cart-footer', ...(!drawer ? ['cart-icon-bubble'] : [])]) {
      const current = document.getElementById(id);
      if (!current || !sections[id]) continue;
      const html = new DOMParser().parseFromString(sections[id], 'text/html');
      const replacement = html.getElementById(id);
      if (replacement) current.innerHTML = replacement.innerHTML;
    }
    syncCheckout(cart);
    if (openDrawer && drawer) {
      drawer.open();
      setTimeout(() => {
        const warning = drawer.querySelector('[data-calendar-cart-warning]');
        warning?.focus({ preventScroll: true });
        warning?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 0);
    } else if (openDrawer && !drawer && window.location.pathname !== config.cartUrl) {
      window.location.assign(config.cartUrl);
    }
  }

  // Add first. If the resulting cart is mixed, explain and resolve it in the drawer.
  // Return the original response so each existing component retains its success behavior.
  function request(url, options) {
    if (!active()) return fetch(url, options);
    return enqueue(async () => {
      const response = await fetch(url, options);
      if (!response.ok) return response;
      try {
        const cart = await readCart();
        syncCheckout(cart);
        if (hasConflict(cart)) await refreshCart(cart, true);
      } catch (error) {
        // The add succeeded: never report it as failed and encourage a duplicate retry.
        syncCheckout(null);
        document.querySelector('cart-drawer')?.open();
        showCartError();
      }
      return response;
    });
  }

  async function keepItems(kind) {
    if (!active() || !['calendar', 'other'].includes(kind)) return;
    return enqueue(async () => {
      const cart = await readCart();
      const updates = {};
      for (const item of cart.items) {
        const keep = kind === 'calendar' ? isCalendarItem(item) : !isCalendarItem(item);
        if (!keep) updates[item.key] = 0;
      }
      if (Object.keys(updates).length) {
        const response = await fetch(`${config.cartUrl}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!response.ok) throw new Error(config.errorMessage);
      }
      const updatedCart = await readCart();
      await refreshCart(updatedCart);
      return updatedCart;
    });
  }

  window.presaleCart = { classifyCart, request, isCalendar, hasCalendar, hasConflict, active, keepItems, canCheckout: () => !active() || !checkoutBlocked };

  let checkingCheckout = false;
  let checkoutValidated = false;
  async function checkCheckout(resume) {
    if (checkingCheckout) return;
    checkingCheckout = true;
    try {
      const cart = await readCart();
      syncCheckout(cart);
      if (hasConflict(cart)) {
        await refreshCart(cart, true);
        return;
      }
      checkoutValidated = true;
      resume();
    } catch (error) {
      syncCheckout(null);
      document.querySelector('cart-drawer')?.open();
      showCartError();
    } finally {
      checkingCheckout = false;
      checkoutValidated = false;
    }
  }

  if (config.enabled) {
    document.addEventListener('click', event => {
      const choice = event.target.closest('[data-calendar-keep]');
      if (choice && active()) {
        event.preventDefault();
        const warning = choice.closest('[data-calendar-cart-warning]');
        if (warning.getAttribute('aria-busy') === 'true') return;
        warning.setAttribute('aria-busy', 'true');
        warning.querySelectorAll('button').forEach(button => { button.disabled = true; });
        keepItems(choice.dataset.calendarKeep)
          .catch(() => { syncCheckout(null); showCartError(); })
          .finally(() => {
            warning.removeAttribute('aria-busy');
            warning.querySelectorAll('button').forEach(button => { button.disabled = false; });
          });
        return;
      }
      if (!active() || checkoutValidated) return;
      const checkout = event.target.closest('[name="checkout"], a[href]');
      if (!checkout || checkout.disabled) return;
      if (checkout.tagName === 'A' && !/\/checkout\/?$/.test(new URL(checkout.href).pathname)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkCheckout(() => checkout.click());
    }, true);
    document.addEventListener('submit', event => {
      if (!active() || checkoutValidated) return;
      if (event.submitter?.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkCheckout(() => event.target.requestSubmit(event.submitter));
    }, true);
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!active() || form.closest('product-form, heimat-card-add') || !/\/cart\/add(?:\.js)?$/.test(new URL(form.action).pathname)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      request(`${config.cartUrl}/add.js`, { method: 'POST', body: new FormData(form) })
        .then(response => {
          if (response.ok && !checkoutBlocked) window.location.assign(config.cartUrl);
          else if (!response.ok) showCartError();
        })
        .catch(showCartError);
    }, true);
  }

  customElements.define('calendar-presale', class extends HTMLElement {});
})();
